import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';

// Hook personnalisé pour gérer le système de notifications
const useNotificationSystem = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    smsForAllNotifications: false,
    reminderDelay: 7
  });
  
  const { currentUser } = useAuth();

  // Charger les notifications de l'utilisateur
  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    console.log('🔔 NOTIFICATION-SYSTEM: Initialisation pour user:', currentUser.uid);

    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', currentUser.uid)  // ✅ CORRIGÉ : userId au lieu de recipientId
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      console.log('📡 NOTIFICATION-SYSTEM: Snapshot reçu avec', snapshot.size, 'documents');
      
      const notificationsData = [];
      let unreadCounter = 0;

      snapshot.forEach((doc) => {
        const notification = { id: doc.id, ...doc.data() };
        notificationsData.push(notification);
        
        // ✅ SYNCHRONISÉ avec useUnreadNotificationsCount : même logique de lecture
        const isRead = notification.lu || notification.read;
        if (!isRead && !notification.deleted && !notification.archived) {
          unreadCounter++;
          console.log('✅ NOTIFICATION-SYSTEM: Notification NON LUE comptée:', notification.title);
        } else if (isRead) {
          console.log('❌ NOTIFICATION-SYSTEM: Notification exclue (déjà lue):', notification.title);
        } else if (notification.deleted) {
          console.log('❌ NOTIFICATION-SYSTEM: Notification exclue (supprimée):', notification.title);
        }
      });

      // Trier par date de création (plus récent en premier)
      notificationsData.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toDate() - a.createdAt.toDate();
        }
        return 0;
      });

      console.log('📊 NOTIFICATION-SYSTEM: Comptage final:', unreadCounter);
      console.log('📊 NOTIFICATION-SYSTEM: Total notifications:', notificationsData.length);

      setNotifications(notificationsData);
      setUnreadCount(unreadCounter);
      setLoading(false);
    }, (error) => {
      console.error('❌ NOTIFICATION-SYSTEM: Erreur lors du chargement des notifications:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Charger les paramètres de notification
  useEffect(() => {
    const loadSettings = async () => {
      if (!currentUser?.uid) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setSettings({
            emailNotifications: userData.emailNotifications !== false,
            smsNotifications: userData.smsNotifications === true,
            pushNotifications: userData.pushNotifications !== false,
            smsForAllNotifications: userData.smsForAllNotifications === true,
            reminderDelay: userData.reminderDelay || 7
          });
        }
      } catch (error) {
        console.error('Erreur chargement paramètres:', error);
      }
    };

    loadSettings();
  }, [currentUser?.uid]);

  // Marquer une notification comme lue
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
        lu: true,  // ✅ AJOUTÉ : Marquer les deux champs pour compatibilité
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Erreur marquage notification comme lue:', error);
      throw error;
    }
  }, []);

  // Marquer toutes les notifications comme lues
  const markAllAsRead = useCallback(async () => {
    try {
      const unreadNotifications = notifications.filter(n => {
        const isRead = n.lu || n.read;
        return !isRead && !n.deleted && !n.archived;  // ✅ SYNCHRONISÉ avec la logique de comptage
      });
      
      const promises = unreadNotifications.map(notification =>
        updateDoc(doc(db, 'notifications', notification.id), {
          read: true,
          lu: true,  // ✅ AJOUTÉ : Marquer les deux champs pour compatibilité
          readAt: serverTimestamp()
        })
      );
      await Promise.all(promises);
    } catch (error) {
      console.error('Erreur marquage toutes notifications comme lues:', error);
      throw error;
    }
  }, [notifications]);

  // Supprimer une notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        deleted: true,
        deletedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Erreur suppression notification:', error);
      throw error;
    }
  }, []);

  // Créer une nouvelle notification
  const createNotification = useCallback(async (notificationData) => {
    try {
      const notification = {
        ...notificationData,
        createdAt: serverTimestamp(),
        read: false,
        lu: false,  // ✅ AJOUTÉ : Initialiser les deux champs pour compatibilité
        deleted: false
      };
      
      const docRef = await addDoc(collection(db, 'notifications'), notification);
      return docRef.id;
    } catch (error) {
      console.error('Erreur création notification:', error);
      throw error;
    }
  }, []);

  // Sauvegarder les paramètres de notification
  const saveSettings = useCallback(async (newSettings) => {
    if (!currentUser?.uid) {
      throw new Error('Utilisateur non connecté');
    }

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      
      // Vérifier si le document existe
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        // Créer le document s'il n'existe pas
        await setDoc(userDocRef, {
          email: currentUser.email,
          displayName: currentUser.displayName,
          ...newSettings,
          createdAt: serverTimestamp()
        });
      } else {
        // Mettre à jour le document existant
        await updateDoc(userDocRef, {
          ...newSettings,
          updatedAt: serverTimestamp()
        });
      }
      
      setSettings(newSettings);
    } catch (error) {
      console.error('Erreur sauvegarde paramètres:', error);
      throw error;
    }
  }, [currentUser]);

  // Obtenir les notifications par type
  const getNotificationsByType = useCallback((type) => {
    return notifications.filter(notification => notification.type === type);
  }, [notifications]);

  // Obtenir les notifications non lues - ✅ SYNCHRONISÉ avec useUnreadNotificationsCount
  const getUnreadNotifications = useCallback(() => {
    return notifications.filter(notification => {
      const isRead = notification.lu || notification.read;
      return !isRead && !notification.deleted && !notification.archived;
    });
  }, [notifications]);

  // Obtenir les notifications récentes (dernières 24h)
  const getRecentNotifications = useCallback(() => {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    
    return notifications.filter(notification => {
      if (notification.createdAt) {
        return notification.createdAt.toDate() > oneDayAgo;
      }
      return false;
    });
  }, [notifications]);

  return {
    // État
    notifications,
    unreadCount,
    loading,
    settings,
    
    // Actions
    markAsRead,
    markAllAsRead,
    deleteNotification,
    createNotification,
    saveSettings,
    
    // Getters
    getNotificationsByType,
    getUnreadNotifications,
    getRecentNotifications
  };
};

export default useNotificationSystem;